import { useState, useEffect } from 'react';
import { Outlet } from 'react-router-dom';
import { styled } from '@mui/material/styles';
import Box from '@mui/material/Box'
import Dialog from '@mui/material/Dialog';
import DialogActions from '@mui/material/DialogActions';
import DialogContent from '@mui/material/DialogContent';
import DialogContentText from '@mui/material/DialogContentText';
import DialogTitle from '@mui/material/DialogTitle';
import Button from '@mui/material/Button';
import Stack from '@mui/material/Stack';
import Typography from '@mui/material/Typography';
import CircularProgress from '@mui/material/CircularProgress';
import Stepper from '@mui/material/Stepper';
import Step from '@mui/material/Step';
import StepLabel from '@mui/material/StepLabel';
import Iconify from '../../components/Iconify';
import { useSelector, useDispatch } from '../../redux/store';
import { getDialog } from '../../redux/slices/dialog'
import { setCreateOnboardingInformation } from '../../redux/thunks/onboardingThunks';
import useCollapseDrawer from '../../hooks/useCollapseDrawer';
import { HEADER, NAVBAR } from '../../config';
import DashboardHeader from './header';
import NavbarVertical from './navbar/NavbarVertical';
import { SectionLoading } from '../../components/SectionLoading';
import { DialogController } from '../../components/dialog';
import { delay } from '../../utils/levarHelpers';

// ----------------------------------------------------------------------

const MainStyle = styled('main', {
  shouldForwardProp: (prop) => prop !== 'collapseClick',
})(({ collapseClick, theme }) => ({
  flexGrow: 1,
  paddingTop: HEADER.MOBILE_HEIGHT + 24,
  paddingBottom: HEADER.MOBILE_HEIGHT + 24,
  [theme.breakpoints.up('lg')]: {
    paddingLeft: 16,
    paddingRight: 16,
    paddingTop: HEADER.DASHBOARD_DESKTOP_HEIGHT + 24,
    paddingBottom: HEADER.DASHBOARD_DESKTOP_HEIGHT + 24,
    width: `calc(100% - ${NAVBAR.DASHBOARD_WIDTH}px)`,
    transition: theme.transitions.create('margin-left', {
      duration: theme.transitions.duration.shorter,
    }),
    ...(collapseClick && {
      marginLeft: NAVBAR.DASHBOARD_COLLAPSE_WIDTH,
    }),
  },
  position: 'relative'
}));

// ----------------------------------------------------------------------

export default function DashboardLayout() {
  const dispatch = useDispatch();
  const { dialogStatus, dialogType, dialogData } = useSelector((state) => state.dialog);
  const { user: { setup_wizard_state: userSetupWizardState } } = useSelector((state) => state.account)
  const { checkout } = useSelector((state) => state.cart)
  const AccountStore = useSelector((state) => state.account.store);
  const { adminStatus } = useSelector((state) => state.admin);

  const [inactivityDialog, setInactivityDialog] = useState(false);
  const [onboardingDialog, setOnboardingDialog] = useState(false);
  const [isProcessing, setIsProcessing] = useState(false);
  const [activeStep, setActiveStep] = useState(0);
  const INACTIVITY_TIMEOUT = 30 * 60 * 1000; // 30 minutes in milliseconds
  // const INACTIVITY_TIMEOUT = 10 * 1000; // 10 seconds
  let inactivityTimer;

  const shopType = sessionStorage.getItem('shopType')

  // console.log('ReduxStore.store_id', AccountStore)

  const { collapseClick, isCollapse } = useCollapseDrawer();
  const [open, setOpen] = useState(false);
  
  // Show onboarding dialog when userSetupWizardState is 7
  useEffect(() => {
    if (userSetupWizardState === 7) {
      setOnboardingDialog(true);
    } else {
      setOnboardingDialog(false);
    }
  }, [userSetupWizardState]);

  // Reset inactivity timer on user activity
  const resetInactivityTimer = () => {
    if (inactivityTimer) {
      clearTimeout(inactivityTimer);
    }
    inactivityTimer = setTimeout(() => {
      setInactivityDialog(true);
    }, INACTIVITY_TIMEOUT);
  };

  // Handle page reload
  const handleReload = () => {
    window.location.reload();
  };

  // Setup activity listeners
  useEffect(() => {
    const activityEvents = ['mousedown', 'keydown', 'scroll', 'mousemove'];
    
    activityEvents.forEach(event => {
      window.addEventListener(event, resetInactivityTimer);
    });

    // Initial timer setup
    resetInactivityTimer();

    // Cleanup
    return () => {
      if (inactivityTimer) {
        clearTimeout(inactivityTimer);
      }
      activityEvents.forEach(event => {
        window.removeEventListener(event, resetInactivityTimer);
      });
    };
  }, []);

  

  // ----------------------
  // Close Dialog Status in Redux
  // ----------------------
  const dialogClose = () => {
    dispatch(getDialog({ dialogStatus: !dialogStatus, dialogType:'', dialogData: '' }));
  };

  const openCart = () => {
    dispatch(getDialog({ dialogStatus: true, dialogType:'cartCheckoutDialog', dialogData: '' }));
  }

  // console.log('AccountStore.store_id', AccountStore.store_id)
  // console.log('adminStatus', adminStatus)
  useEffect(() => {
    const handleDialog = async () => {
      if (shopType === 'mfr') {
        await delay(2000);
        dispatch(getDialog({ dialogStatus: true, dialogType: 'productCSVUploadDialog', dialogData: '' }));
      } 
      // else if(!AccountStore.store_id && !adminStatus) {
      //   await delay(1000);
      //   console.log('HERERE')
      //   dispatch(getDialog({ dialogStatus: true, dialogType: 'customStoreCreationDialog', dialogData: '' }));
      // }
    };

    handleDialog();
  }, [shopType, dispatch, AccountStore]);

  // Handle onboarding process
  const handleStartOnboarding = async () => {
    try {
      setIsProcessing(true);
      setActiveStep(1); // Move to processing step
      
      const result = await dispatch(setCreateOnboardingInformation());

      if (result.success) {
        setIsProcessing(false);
        setActiveStep(2); // Move to completion step
      }
    } catch (error) {
      console.error("Error during onboarding:", error);
      setIsProcessing(false);
    }
  };

  // Steps for the onboarding process
  const steps = ['Setup', 'Processing', 'Complete'];
  
  // Handle navigation to explore sample model
  const handleExploreModel = () => {
    setOnboardingDialog(false);
    // Add navigation to sample model page if needed
    // history.push('/dashboard/models/sample');
  };

  return (
    <Box
      sx={{
        display: { lg: 'flex' },
        minHeight: { lg: 1 },
      }}
    >
      {/* Main Loading Screen */}
      <SectionLoading />

      {/* Top Right Account */}
      <DashboardHeader 
        isCollapse={isCollapse}
        usersetupwizardstate={userSetupWizardState}
        onOpenSidebar={() => setOpen(true)} 
        openCart={openCart}
        cartTotal={checkout.cart.length}
      />

      {/* Left Navigation */}
      <NavbarVertical 
        isOpenSidebar={open}
        onCloseSidebar={() => setOpen(false)} 
      />
      
      {/* Main Content */}
      <MainStyle collapseClick={collapseClick}>
        <Outlet />
      </MainStyle>

      {/* Main Modal */}
      <DialogController 
        open={dialogStatus} 
        onClose={dialogClose} 
        dialogType={dialogType}
        dialogData={dialogData}
      />

      {/* Inactivity Dialog */}
      <Dialog
        open={inactivityDialog}
        onClose={() => {}} // Prevent closing on backdrop click
        disableEscapeKeyDown // Prevent closing on Escape key
      >
        <DialogTitle>Session Inactive</DialogTitle>
        <DialogContent>
          <DialogContentText>
            Your user session has been inactive. Please reload the page to ensure everything is up to date.
          </DialogContentText>
        </DialogContent>
        <DialogActions>
          <Button onClick={handleReload} variant="contained" color="primary" fullWidth>
            Reload Page
          </Button>
        </DialogActions>
      </Dialog>

      {/* START OF ONBOARDING SAMPLE MODEL */}
      <Dialog
        open={onboardingDialog}
        onClose={() => {}}
        disableEscapeKeyDown
        maxWidth="sm"
        fullWidth
        BackdropProps={{
          style: { backgroundColor: 'rgba(0, 0, 0, 0.8)' }
        }}
      >
        <DialogTitle sx={{ mb: 2 }}>
          {activeStep === 2 ? 'Welcome to LEVAR!' : 'Activate Your Free LEVAR Account'}
        </DialogTitle>
        
        <DialogContent>
          <Stepper activeStep={activeStep} sx={{ mb: 3 }}>
            {steps.map((label) => (
              <Step key={label}>
                <StepLabel>{label}</StepLabel>
              </Step>
            ))}
          </Stepper>
          
          {activeStep === 0 && (
            <Stack spacing={3}>
              <DialogContentText>
                Welcome to LEVAR! We're setting up your free tier account which includes access to our 3D visualization platform with a sample model ready to explore.
              </DialogContentText>
              
              <Box sx={{ 
                backgroundColor: 'background.neutral',
                p: 2,
                borderRadius: 1
              }}>
                <Typography variant="subtitle1" gutterBottom fontWeight="bold">
                  Your Free Tier Benefits:
                </Typography>
                <Stack spacing={1.5} sx={{ pl: 2 }}>
                  <Typography variant="body2">• One free 3D model upload for your store</Typography>
                  <Typography variant="body2">• Pre-loaded sample model to explore LEVAR features</Typography>
                  <Typography variant="body2">• Easy integration with your existing store</Typography>
                </Stack>
              </Box>
              
              <Typography variant="body2" color="primary.dark" sx={{ fontWeight: 'medium' }}>
                By activating your account, you'll have immediate access to test and use our 3D visualization technology in your store.
              </Typography>
            </Stack>
          )}
          
          {activeStep === 1 && (
            <Stack spacing={3}>
              <Box sx={{ 
                display: 'flex', 
                justifyContent: 'center', 
                alignItems: 'center',
                flexDirection: 'column',
                gap: 2,
                backgroundColor: 'background.neutral',
                p: 5,
                borderRadius: 1
              }}>
                <CircularProgress color="primary" />
                <Typography variant="body2" color="text.secondary">
                  Setting up your environment... This may take a moment.
                </Typography>
              </Box>
              
              <Box sx={{ px: 2 }}>
                <Typography variant="body2" gutterBottom>Setting up your account...</Typography>
                <Typography variant="body2" gutterBottom>Creating your first project & loadout...</Typography>
                <Typography variant="body2" gutterBottom>Preparing sample model...</Typography>
                <Typography variant="body2">Configuring your store...</Typography>
              </Box>
            </Stack>
          )}
          
          {activeStep === 2 && (
            <Stack>
              <Box sx={{ 
                display: 'flex', 
                justifyContent: 'center', 
              }}>
                <Iconify icon="mdi:check-circle" color="green" sx={{ fontSize: 60 }} />
              </Box>
              
              <Typography variant="h6" align="center" color="primary.main" gutterBottom>
                Your account is ready!
              </Typography>
              
              <Typography variant="body2" align="center" paragraph>
                Your free LEVAR account is now active with a sample model ready to explore.
              </Typography>
              
              <Box sx={{ 
                backgroundColor: 'background.neutral',
                p: 2,
                borderRadius: 1
              }}>
                <Typography variant="subtitle1" gutterBottom fontWeight="bold">
                  What you can do now:
                </Typography>
                <Stack spacing={2}>
                  <Box>
                    <Typography variant="body2" fontWeight="bold">✓ Explore sample model</Typography>
                    <Typography variant="body2" color="text.secondary">
                      Check out our pre-loaded sample to see how 3D works on your store
                    </Typography>
                  </Box>
                  
                  <Box>
                    <Typography variant="body2" fontWeight="bold">✓ Upload your own 3D model</Typography>
                    <Typography variant="body2" color="text.secondary">
                      Your free tier includes one model upload
                    </Typography>
                  </Box>
                  
                  <Box>
                    <Typography variant="body2" fontWeight="bold">✓ Convert your products to 3D</Typography>
                    <Typography variant="body2" color="text.secondary">
                      Use our services to create professional 3D models
                    </Typography>
                  </Box>
                  
                  <Box>
                    <Typography variant="body2" fontWeight="bold">✓ Subscribe for more</Typography>
                    <Typography variant="body2" color="text.secondary">
                      Upgrade to add unlimited models to your store or create your own 3D models
                    </Typography>
                  </Box>
                </Stack>
              </Box>
            </Stack>
          )}
        </DialogContent>
        
        <DialogActions sx={{ px: 3, pb: 3, pt: activeStep === 2 ? 1 : 0 }}>
          {activeStep === 0 && (
            <Button 
              onClick={handleStartOnboarding} 
              variant="contained" 
              color="primary"
              sx={{ fontWeight: 'bold' }}
              fullWidth
            >
              Activate Free Account
            </Button>
          )}
          
          {activeStep === 1 && (
            <Button 
              disabled
              variant="outlined" 
              color="primary"
              fullWidth
            >
              Setting Up Your Account...
            </Button>
          )}
          
          {activeStep === 2 && (
            <Stack direction="row" spacing={2} sx={{ width: '100%' }}>
              <Button 
                href="https://mac-scot-stuff.myshopify.com/collections/products"
                target="_blank"
                rel="noopener noreferrer"
                variant="outlined" 
                color="primary"
                sx={{ flex: 1 }}
              >
                Examples of LEVAR in Action
              </Button>
              <Button 
                onClick={() => setOnboardingDialog(false)}
                variant="contained" 
                color="primary"
                sx={{ flex: 1, fontWeight: 'bold' }}
              >
                Start Using LEVAR
              </Button>
            </Stack>
          )}
        </DialogActions>
      </Dialog>
      {/* END OF ONBOARDING SAMPLE MODEL */}

    </Box>
  );
}
